import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TabNav, Badge, Button, BackButton, ListItem, Pagination, Card, ReservationSummary, SearchBar, Spinner, SuccessAlertModal } from '../../../components/common';
import ReviewRegisterModal from '../../../components/service/ReviewRegisterModal';
import { reservationService, userService, reviewService } from '../../../api/services';
import { reservationTabs, myPageTabs } from '../../../constants/uiConstants';
import { useMoney } from '../../../hooks/useMoney';
import { formatReservationDate, formatDateTimeLocale } from '../../../utils/formatDate';
import { getBadgeServiceType } from '../../../constants/serviceLabels';

function makeMappers(t) {
  const SERVICE_LABELS = {
    airport: t('nav.private_transfer'), golf: t('nav.private_transfer'), ski: t('nav.private_transfer'),
    hospital: t('nav.private_transfer'), pet: t('nav.private_transfer'), business: t('nav.private_transfer'),
    tour: t('nav.private_tour'), shopping: t('nav.private_shopping'),
  };
  const SERVICE_TYPE_KR = {
    airport: t('service.airport'), golf: t('service.golf'), ski: t('service.ski'),
    hospital: t('service.hospital'), pet: t('service.pet'), business: t('service.business'),
    tour: t('service.tour'), shopping: t('service.shopping'),
  };
  const STATUS_LABELS = {
    pending: t('reservation.pending'), confirmed: t('reservation.confirmed'),
    driver_arrived: t('reservation.driver_arrived'), driving: t('reservation.driving'),
    drive_completed: t('reservation.drive_completed'), completed: t('reservation.completed'),
    cancelled: t('reservation.cancelled'), refunded: t('reservation.refunded'), no_show: t('reservation.no_show'),
  };
  return { SERVICE_LABELS, SERVICE_TYPE_KR, STATUS_LABELS };
}

function mapReservation(r, t) {
  const { SERVICE_LABELS, SERVICE_TYPE_KR, STATUS_LABELS } = makeMappers(t);
  return {
    id: r.id,
    service: SERVICE_LABELS[r.service_type] || t('nav.private_transfer'),
    serviceType: getBadgeServiceType(r.service_type),
    status: STATUS_LABELS[r.status] || r.status,
    paymentDate: r.created_at ? formatDateTimeLocale(r.created_at) + ' ' + t('complete.reservation_paid') : '-',
    title: r.service_type === 'tour'
      ? (r.tour_name || t('nav.private_tour'))
      : t('reservation.private_transfer_dash', { type: SERVICE_TYPE_KR[r.destination] || SERVICE_TYPE_KR[r.service_type] || r.destination || '' }),
    reservationNo: r.reservation_number,
    scheduledDate: formatReservationDate(r.reservation_date, r.start_time),
    amount: r.total_price || 0,
    vehicle: r.vehicle_name || null,
    surchargeStatus: r.surcharge_status || null,
    reviewStatus: r.review_status || 'none',
    rawStatus: r.status,
    // 정렬용 raw 타임스탬프 — 머지 후 _sortAt DESC 로 최신 활동순 정렬에 사용.
    // BE Reservation 응답에 updated_at 미포함이라 created_at 사용. 결제 확정 시점과
    // 예약 생성 시점은 보통 분 단위 이내라 사용자 체감 "결제 완료 최신순" 과 일치.
    _sortAt: r.created_at || null,
  };
}

const TAB_STATUS = {
  all: '',
  upcoming: 'pending,confirmed,driver_arrived,driving',
  completed: 'drive_completed,completed',
  refunded: 'cancelled,refunded,no_show',
};

export default function ReservationList() {
  const { t } = useTranslation();
  const money = useMoney();
  const navigate = useNavigate();
  const location = useLocation();

  // #101: 비로그인 시 로그인 페이지로 리다이렉트
  useEffect(() => {
    if (!localStorage.getItem('accessToken')) {
      navigate('/login', { state: { from: location }, replace: true });
    }
  }, [navigate, location]);

  const [activeTab, setActiveTab] = useState(location.state?.tab || 'all');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [waitlistItems, setWaitlistItems] = useState([]);
  // 후기 등록 후 목록 refetch 트리거 — review_status='registered' 반영되어 버튼이 '후기 등록 완료'로 전환.
  const [refreshTick, setRefreshTick] = useState(0);
  // BE Reservation 응답에 review_status 필드가 아직 없어 refetch 만으론 버튼 전환 안 됨.
  // 이번 세션에서 등록 성공한 reservation id 를 클라이언트 측 Set 으로 추적해 즉시 UI 반영.
  const [locallyReviewed, setLocallyReviewed] = useState(() => new Set());
  // 후기 등록 성공 알림 — SuccessAlertModal 공통 컴포넌트로 통일 (네이티브 alert 대체).
  const [showReviewSuccess, setShowReviewSuccess] = useState(false);

  useEffect(() => {
    setLoading(true);
    const statusFilter = TAB_STATUS[activeTab];
    reservationService.getList({ ...(statusFilter ? { status: statusFilter } : {}), page: currentPage, size: 10 })
      .then((data) => {
        setItems((data.items || data || []).map((r) => mapReservation(r, t)));
        setTotalPages(data.pages || 1);
      })
      .catch(() => { setItems([]); setTotalPages(1); })
      .finally(() => setLoading(false));
  }, [activeTab, currentPage, t, refreshTick]);

  // 후기 등록 — ReviewRegisterModal 의 onSubmit 콜백이 실제 API 호출까지 수행하지 않던 회귀.
  // reviewService.create 호출 → 성공 시 모달 닫고 알림 + 목록 새로고침으로 버튼 상태 동기화.
  const handleReviewSubmit = async ({ rating, text }) => {
    if (!reviewTarget || submitting) return;
    if (!text?.trim()) { alert(t('common.review_write_placeholder')); return; }
    if (!rating || rating < 1) { alert(t('common.review_rating')); return; }
    setSubmitting(true);
    try {
      await reviewService.create({ reservation_id: reviewTarget.id, rating, content: text });
      const submittedId = reviewTarget.id;
      setReviewTarget(null);
      setLocallyReviewed((prev) => {
        const next = new Set(prev);
        next.add(submittedId);
        return next;
      });
      setRefreshTick((c) => c + 1);
      setShowReviewSuccess(true);
    } catch (err) {
      alert(typeof err.message === 'string' ? err.message : t('reservation.review_submit_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  // 대기 등록 내역 로드
  useEffect(() => {
    userService.getMyWaitlist?.()
      .then((data) => setWaitlistItems(Array.isArray(data) ? data : (data?.items || [])))
      .catch(() => {});
  }, []);

  // 대기 등록 건을 예약 카드와 동일한 형태로 매핑
  const { SERVICE_LABELS, SERVICE_TYPE_KR } = makeMappers(t);
  const WAITLIST_STATUS = {
    waiting: t('reservation.waiting_status'),
    notified: t('reservation.waiting_notified'),
    converted: t('reservation.waiting_converted'),
    cancelled: t('reservation.waiting_cancelled'),
  };
  const waitlistCards = (activeTab === 'upcoming' || activeTab === 'all' ? waitlistItems : [])
    .filter((w) => w.status === 'waiting' || w.status === 'notified')
    .map((w) => ({
      id: `waitlist-${w.id}`,
      isWaitlist: true,
      service: SERVICE_LABELS[w.service_type] || t('nav.private_transfer'),
      serviceType: getBadgeServiceType(w.service_type),
      status: WAITLIST_STATUS[w.status] || w.status,
      paymentDate: w.created_at ? formatDateTimeLocale(w.created_at) + ' ' + t('reservation.registered') : '',
      title: t('reservation.private_transfer_dash', { type: SERVICE_TYPE_KR[w.service_type] || w.destination || w.service_type }),
      reservationNo: null,
      scheduledDate: formatReservationDate(w.desired_date, w.desired_time),
      amount: '-',
      _sortAt: w.created_at || null,
    }));

  // 머지 후 _sortAt DESC 정렬 (전체/이용예정/이용완료/환불 4탭 공통 — 최신 활동순).
  // 기존엔 waitlist 가 무조건 앞에 와서 결제 확정한 신규 예약이 카드 더미 뒤로 밀리는 회귀.
  const allItems = [...waitlistCards, ...items].sort((a, b) => {
    const ta = a._sortAt ? new Date(a._sortAt).getTime() : 0;
    const tb = b._sortAt ? new Date(b._sortAt).getTime() : 0;
    return tb - ta;
  });
  const filtered = searchQuery
    ? allItems.filter((r) => r.title?.includes(searchQuery) || r.reservationNo?.includes(searchQuery))
    : allItems;
  const isRefunded = activeTab === 'refunded';
  const isCompleted = activeTab === 'completed';

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setCurrentPage(1);
    setSearchQuery('');
  };

  // 마이페이지 섹션 탭 이동 — 예약 내역 (현재) ↔ 계정 관리 ↔ 후기 관리
  const handleMyPageTabChange = (tabId) => {
    if (tabId === 'account') navigate('/mypage/account');
    else if (tabId === 'reviews') navigate('/mypage/reviews');
    // 'reservation' 은 현재 페이지 — no-op
  };

  return (
    <div className="bg-mono-50 min-h-screen">
      {/* 마이페이지 섹션 탭 — MyAccount/MyReviews 와 동일 패턴 (예약 내역·계정·후기) */}
      <TabNav tabs={myPageTabs} activeTab="reservation" onTabChange={handleMyPageTabChange} />
      {/* 예약 상태 필터 탭 (전체·예정·완료·환불) */}
      <TabNav
        variant="fullwidth"
        tabs={reservationTabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />

      <div className="max-w-content mx-auto px-2.5 sm:px-0 py-6 sm:py-8 space-y-4">
        <div className="flex items-center"><BackButton /></div>
        {/* Search bar (inline, same spacing as cards) */}
        <SearchBar
          placeholder={t('reservation.search_placeholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />

        {/* Reservation Cards */}
        <div className="space-y-4">
          {loading && <div className="flex justify-center py-8"><Spinner /></div>}
          {filtered.map((item) => (
            <Card
              key={item.id}
              variant="padded"
              size="lg"
              interactive
              onClick={() => !item.isWaitlist && navigate(`/reservation/${item.id}`)}
            >
              <ReservationSummary
                service={item.service}
                serviceType={item.serviceType}
                status={item.status}
                paymentDate={item.paymentDate}
                title={item.title}
                reservationNo={item.reservationNo}
                scheduledDate={!isRefunded ? item.scheduledDate : null}
              />

              {/* Total Amount / 대기 등록 안내 */}
              {item.isWaitlist ? (
                <div className="flex items-center justify-between pt-3 border-t border-mono-100">
                  <span className="text-2sm font-medium text-mono-400">{t('reservation.waitlist_notice')}</span>
                </div>
              ) : (
                <div className="flex items-center justify-between pt-3 border-t border-mono-100">
                  <span className="text-sm font-bold text-mono-900">
                    {isRefunded ? t('reservation.total_refund') : t('reservation.total_amount')}
                  </span>
                  <p className="flex items-baseline gap-0.5">
                    <span className="text-xl font-bold text-mono-900">{money(item.amount)}</span>
                  </p>
                </div>
              )}

              {/* Action buttons for completed tab */}
              {isCompleted && (
                <div onClick={(e) => e.stopPropagation()}>
                  {item.surchargeStatus === 'pending' ? (
                    <Button fullWidth onClick={() => navigate(`/reservation/${item.id}/surcharge`)}>
                      {t('reservation.extra_charge_payment')}
                    </Button>
                  ) : (item.reviewStatus === 'registered' || locallyReviewed.has(item.id)) ? (
                    <Button level="level4" fullWidth disabled>
                      {t('reservation.review_registered')}
                    </Button>
                  ) : (
                    <Button level="outline-primary" fullWidth onClick={() => setReviewTarget(item)}>
                      {t('reservation.register_review')}
                    </Button>
                  )}
                </div>
              )}
            </Card>
          ))}

          {!loading && filtered.length === 0 && (
            <div className="py-16 text-center">
              <p className="text-sm text-mono-400">{t('reservation.no_reservation')}</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <Pagination
            current={currentPage}
            total={totalPages}
            onChange={setCurrentPage}
            className="mt-8"
          />
        )}
      </div>

      {/* Review modal */}
      {reviewTarget && (
        <ReviewRegisterModal
          reservation={reviewTarget}
          onClose={() => { if (!submitting) setReviewTarget(null); }}
          onSubmit={handleReviewSubmit}
          submitting={submitting}
        />
      )}

      {/* 후기 등록 성공 알림 — 네이티브 alert 대신 공통 모달 */}
      <SuccessAlertModal
        open={showReviewSuccess}
        title={t('reservation.review_registered')}
        message={t('reservation.review_submitted')}
        onClose={() => setShowReviewSuccess(false)}
      />
    </div>
  );
}
